export const caseStudies = [
  {
    slug: "snap-app",
    index: "01",
    title: "SNAP — Automated Event Planning Software",
    category: "Product · Enterprise SaaS",
    year: "2024 / 2025",
    role: "Lead UX / UI Designer",
    timeline: "Ongoing",
    deliverables: ["End-to-end UX", "Procurement Workflow", "AI-assisted RFP"],
    client: "Nunify",
    cover: "https://customer-assets.emergentagent.com/job_portfolio-showcase-2863/artifacts/imkisnhe_Rectangle%20697.png",
    accent: "#F26A21",
    intro:
      "SNAP unifies RFI, RFQ, RFP and SOW with task management, vendor selection, budgeting and event creation into one conversational, step-by-step workflow. A procurement manager can move from request to award without leaving the canvas.",
    problem:
      "Event teams were juggling fragmented tools: Olive, Aura and Siemens for RFQ; Jira, Monday and Asana for tasks. The result was manual vendor requests, unclear approval status, poor budget visibility and complex RFI / RFP / RFQ processes that demanded excessive follow-up and coordination.",
    process:
      "Self-driven research showed event planning is a long procurement story. Stakeholder interviews, competitive audit (Olive, Aura, Siemens, Jira, Monday, Asana), identifying top user jobs (plan an event, request vendors, track budget), developing personas (organisers, admins, finance), framing How-Might-We questions, then drafting, testing and iterating on SNAP screens with internal stakeholders and early users.",
    solution:
      "A conversational, step-by-step experience where each screen focuses on one clear action. AI-assisted RFP creation lets users upload an existing document or PDF and have the structure extracted automatically. The home screen surfaces priorities and next actions; RFP, vendor selection, budget and task screens connect into a single procurement spine.",
    outcomes: [
      { metric: "1 spine", label: "RFI → RFQ → RFP → SOW unified" },
      { metric: "AI-assisted", label: "Document-to-RFP extraction" },
      { metric: "End-to-end", label: "Owned UX from research to handoff" },
    ],
    targetAudience:
      "Gen-Z professionals aged 18 to 30 working in event planning and operations. Digitally native users who are impatient with slow systems and expect fast, intuitive, conversational experiences with minimal effort.",
    competitiveContext:
      "Olive, Aura and Siemens focus on RFQ workflows. Jira, Monday and Asana specialise in task management. No single SaaS product combines RFI, RFQ, RFP and SOW with task management, vendor selection, budgeting and event creation in one unified platform. That is where SNAP's end-to-end value lives.",
    challenges:
      "I initially assumed RFP was a standalone feature. The deeper I researched, the clearer it became that RFP is one stage in a larger procurement process spanning RFI, RFQ and SOW. Translating that interconnected process into a clear digital experience was not straightforward, so I started by designing the RFP flow, then gradually expanded the system to include the other stages, keeping each step simple and understandable.",
    learnings:
      "Through iteration, the layout had to become more minimal to reduce cognitive load. Procurement managers are the primary persona shown here; event planners, finance managers, vendors and approvers are part of the same workflow but appear in adjacent case studies.",
    keyScreens: [
      {
        title: "Home Screen",
        body:
          "A clear overview of priorities and next actions: pending approvals, alerts, upcoming events. Users immediately understand status and stay focused on what matters most.",
      },
      {
        title: "About RFP",
        body:
          "Guides users through defining requirements in a clear, step-by-step manner. Complex documentation broken into reviewable sections with inline progress and AI-extracted fields.",
      },
      {
        title: "Vendor Selection",
        body:
          "A single screen to compare vendor responses, view performance metrics, and award the right partner without opening five other tabs.",
      },
      {
        title: "Budget & Tasks",
        body:
          "Projected estimates, total spend versus budget, risk flags and approval status. The numbers that matter, in the order managers ask for them.",
      },
    ],
    gallery: [
      "https://customer-assets.emergentagent.com/job_portfolio-showcase-2863/artifacts/imkisnhe_Rectangle%20697.png",
      {
        src: "https://customer-assets.emergentagent.com/job_portfolio-showcase-2863/artifacts/o88c3po2_After%20sign-in.png",
        fit: "cover",
        bg: "#F4F4F2",
        position: "left center",
      },
    ],
    externalLink: {
      label: "View full SNAP prototype on Figma",
      href: "https://www.figma.com/proto/z1cRqkyFDqWaSCd4JSgsWY/Portfolio-projects-2025?node-id=8-23020&page-id=8%3A23001",
    },
  },
  {
    slug: "conversational-survey",
    index: "02",
    title: "Conversational Survey — Voice-First Feedback for Events",
    category: "AI Interaction · Concept",
    year: "2025",
    role: "UI / UX Concept · AI Interaction Design",
    timeline: "Concept project",
    deliverables: ["Voice-AI Survey", "Admin Builder", "Analyst Dashboard"],
    client: "Nunify",
    cover: "https://customer-assets.emergentagent.com/job_portfolio-showcase-2863/artifacts/g2uf0hx7_1280x832%201.png",
    coverFit: "contain",
    accent: "#7A3FF0",
    intro:
      "Instead of asking attendees to fill long forms, Conversational Survey lets them speak freely like a short chat. AI listens, captures intent and sentiment, and fills the survey in the background. It turns feedback from a chore into a thirty-second conversation.",
    problem:
      "Attendees are tired after events, so most people skip surveys or feedback comes in very late. Organisers (event managers, community leads, marketing teams) lose the signal they need to prove success and improve the next event. Existing tools rely on long forms that punish honesty.",
    process:
      "Self-driven research instead of competitor benchmarking, because no one was approaching the problem in the same way (the closest was Highbar). We followed a Discover, Define, Develop, Deliver path with stakeholder interviews, a competitive audit, identified jobs (run an event, collect honest feedback, prove ROI), built the Event Organiser persona, sketched conversational flows on paper, and validated with attendees and organisers.",
    solution:
      "A conversational, voice-based survey system. Three named AI roles guide the experience: Odin learns the survey structure from an uploaded or dictated document and transforms it into a voice flow before going live; Maya observes the live conversation between attendees and AI; Max analyses responses and surfaces insights afterwards. Attendees speak, AI captures intent, sentiment and key details in real time.",
    outcomes: [
      { metric: "Voice-first", label: "Speak instead of type, in 30 seconds" },
      { metric: "3 AI roles", label: "Odin (build), Maya (observe), Max (analyse)" },
      { metric: "Same data", label: "Organisers keep the survey shape they trust" },
    ],
    targetAudience:
      "Event organisers: event managers, community leads and marketing teams who run events and need better, more reliable feedback to prove success and improve future events.",
    competitiveContext:
      "We have not found any competitors currently approaching this problem in the same way. The closest is Highbar. Most existing tools optimise for the organiser's report, not the attendee's experience, so attendees skip them and the report goes empty.",
    challenges:
      "Translating a free-form voice conversation into structured survey data without losing the warmth of how attendees actually speak. The AI had to capture intent, sentiment and key details in real time, while keeping the organiser's existing question structure intact so reports still made sense.",
    learnings:
      "The experience can be improved further by sharpening tone-detection accuracy and supporting more languages. Voice is a powerful unlock when paired with a familiar admin builder; without that anchor, organisers don't trust the data.",
    keyScreens: [
      {
        title: "Admin Builder",
        body:
          "Survey Name, response progress, and three AI roles in the sidebar. Organisers upload, paste or dictate questions and Odin learns the structure before going live.",
      },
      {
        title: "Maya (AI Assistant)",
        body:
          "Observes live conversations between attendees and the AI. Surfaces what users are asking, how they respond, and where they struggle so organisers can read the room in real time.",
      },
      {
        title: "Max (Analyst)",
        body:
          "After the event, Max produces an analysis: total responses, response rate, satisfaction score, sentiment breakdown and NPS, exportable in one click.",
      },
      {
        title: "Attendee Chat",
        body:
          "A thirty-second voice chat on mobile. The AI captures intent, sentiment and key details in real time, removing the friction of traditional survey forms.",
      },
    ],
    gallery: [
      "https://customer-assets.emergentagent.com/job_portfolio-showcase-2863/artifacts/g2uf0hx7_1280x832%201.png",
      {
        src: "https://customer-assets.emergentagent.com/job_portfolio-showcase-2863/artifacts/zuwyrjh6_Frame%201000006207.png",
        fit: "cover",
        bg: "#EFE6F7",
      },
    ],
    externalLink: {
      label: "View full Conversational Survey prototype on Figma",
      href: "https://www.figma.com/proto/z1cRqkyFDqWaSCd4JSgsWY/Portfolio-projects-2025?node-id=41-141&page-id=41%3A136",
    },
  },
];

export const getCaseStudy = (slug) =>
  caseStudies.find((c) => c.slug === slug) || null;
